import React, { useContext, useEffect, useState } from "react";
import {
    Avatar,
    Box,
    CssBaseline,
    Divider,
    IconButton,
    Link,
    List,
    Menu,
    MenuItem,
    Stack,
    ThemeProvider,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import MuiAppBar from "@mui/material/AppBar";
import MuiDrawer from "@mui/material/Drawer";
import { styled, useTheme } from "@mui/material/styles";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useNavigate } from "react-router";

import { logoutApi } from "../api/authApi";
import AdministratorMenu from "../components/AdministratorMenu";
import MobileSidebar from "../components/MobileSidebar";
import DarkModeSwitch from "../components/DarkModeSwitch";
import LanguageSwitch from "../components/LanguageSwitch";
import { LanguageContext } from "../components/LanguageContext";
import { ReactComponent as Logo } from "../assets/KaproSCMIcon.svg";
import semenarnaLogo from "../assets/semenarna.png";
import { darkTheme, lightTheme } from "../theme";

const DRAWER_WIDTH = 268;
const COLLAPSED_WIDTH = 76;

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== "open" && prop !== "isMobile",
})(({ theme, open, isMobile }) => ({
    zIndex: theme.zIndex.drawer + 1,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(isMobile
        ? {}
        : open
        ? {
              marginLeft: DRAWER_WIDTH,
              width: `calc(100% - ${DRAWER_WIDTH}px)`,
              transition: theme.transitions.create(["width", "margin"], {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
              }),
          }
        : {
              marginLeft: COLLAPSED_WIDTH,
              width: `calc(100% - ${COLLAPSED_WIDTH}px)`,
          }),
}));

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    "& .MuiDrawer-paper": {
        position: "fixed",
        whiteSpace: "nowrap",
        width: DRAWER_WIDTH,
        height: "100vh",
        top: 0,
        left: 0,
        overflowY: "hidden",
        backgroundImage: "none",
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        ...(!open && {
            overflowX: "hidden",
            width: COLLAPSED_WIDTH,
            transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
        }),
    },
}));

const Layout = ({ children }) => {
    const navigate = useNavigate();
    const { translations } = useContext(LanguageContext) || {};
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));

    const [mode, setMode] = useState(() =>
        localStorage.getItem("theme") === "dark" ? "dark" : "light"
    );
    const theme = mode === "dark" ? darkTheme : lightTheme;

    const [open, setOpen] = useState(!isMobile);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    useEffect(() => {
        if (isMobile) {
            setOpen(false);
        } else {
            setMobileOpen(false);
        }
    }, [isMobile]);

    const toggleDrawer = () => {
        if (isMobile) setMobileOpen((v) => !v);
        else setOpen((v) => !v);
    };

    const handleToggleTheme = () => {
        setMode((prev) => {
            const next = prev === "dark" ? "light" : "dark";
            localStorage.setItem("theme", next);
            return next;
        });
    };

    const handleProfileMenuOpen = (e) => setAnchorEl(e.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const userId = localStorage.getItem("user_id");

    const handleLogout = async (e) => {
        e.preventDefault();
        await logoutApi();
        setMode("light");
        localStorage.setItem("theme", "light");
        navigate("../");
    };

    const renderProfileMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            slotProps={{ paper: { sx: { mt: 1, minWidth: 220 } } }}
        >
            <MenuItem
                onClick={() => {
                    navigate(`/profil/${userId}`);
                    handleMenuClose();
                }}
            >
                <PersonOutlineRoundedIcon
                    sx={{ mr: 1.5, color: "text.secondary" }}
                    fontSize="small"
                />
                <Typography variant="body2" fontWeight={500}>
                    {translations?.adminProfile || "Profile"}
                </Typography>
            </MenuItem>
            <MenuItem
                onClick={() => {
                    navigate("/podešavanja");
                    handleMenuClose();
                }}
            >
                <SettingsOutlinedIcon
                    sx={{ mr: 1.5, color: "text.secondary" }}
                    fontSize="small"
                />
                <Typography variant="body2" fontWeight={500}>
                    {translations?.adminSettings || "Settings"}
                </Typography>
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={handleLogout}>
                <LogoutRoundedIcon
                    sx={{ mr: 1.5, color: "error.main" }}
                    fontSize="small"
                />
                <Typography variant="body2" fontWeight={500} color="error.main">
                    {translations?.adminLogout || "Logout"}
                </Typography>
            </MenuItem>
        </Menu>
    );

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ display: "flex", minHeight: "100vh" }}>
                <AppBar position="fixed" open={open} isMobile={isMobile}>
                    <Toolbar sx={{ minHeight: { xs: 64, sm: 68 }, px: { xs: 2, sm: 3 } }}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="toggle drawer"
                            onClick={toggleDrawer}
                            sx={{
                                mr: 2,
                                ...(!isMobile && open && { display: "none" }),
                            }}
                        >
                            <MenuRoundedIcon />
                        </IconButton>

                        <Typography
                            component="h1"
                            variant="h6"
                            noWrap
                            sx={{ flexGrow: 1, fontWeight: 600 }}
                        >
                            {translations?.adminDashboard || "Dashboard"}
                        </Typography>

                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Tooltip title={translations?.toggleTheme || "Toggle theme"}>
                                <Box>
                                    <DarkModeSwitch
                                        onChange={handleToggleTheme}
                                        checked={mode === "dark"}
                                    />
                                </Box>
                            </Tooltip>
                            <LanguageSwitch />
                            <Tooltip title={translations?.accountMenu || "Account"}>
                                <IconButton
                                    onClick={handleProfileMenuOpen}
                                    sx={{ p: 0.5 }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            bgcolor: "primary.main",
                                            color: "primary.contrastText",
                                        }}
                                    >
                                        <PersonOutlineRoundedIcon fontSize="small" />
                                    </Avatar>
                                </IconButton>
                            </Tooltip>
                            {renderProfileMenu}
                        </Stack>
                    </Toolbar>
                </AppBar>

                {isMobile ? (
                    <MobileSidebar
                        open={mobileOpen}
                        onClose={() => setMobileOpen(false)}
                    />
                ) : (
                    <Drawer variant="permanent" open={open}>
                        <Toolbar
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: open ? "space-between" : "center",
                                px: open ? 2 : 1,
                                minHeight: { xs: 64, sm: 68 },
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                                sx={{
                                    overflow: "hidden",
                                    flexGrow: 1,
                                    justifyContent: open ? "flex-start" : "center",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Logo style={{ width: "100%", height: "100%" }} />
                                </Box>
                                {open && (
                                    <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 700, letterSpacing: "0.2px" }}
                                        noWrap
                                    >
                                        KaproSCM
                                    </Typography>
                                )}
                            </Stack>
                            {open && (
                                <IconButton onClick={toggleDrawer} edge="end" size="small">
                                    <ChevronLeftRoundedIcon />
                                </IconButton>
                            )}
                        </Toolbar>

                        <Divider />

                        <Box
                            sx={{
                                flex: 1,
                                overflowY: "auto",
                                overflowX: "hidden",
                                px: 1,
                                py: 1.5,
                            }}
                        >
                            <List
                                component="nav"
                                sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                            >
                                <AdministratorMenu collapsed={!open} />
                            </List>
                        </Box>

                        {open && (
                            <Box sx={{ p: 1.5 }}>
                                <Link
                                    href="https://www.semenarna.si/"
                                    target="_blank"
                                    rel="noopener"
                                    sx={(theme) => ({
                                        display: "flex",
                                        justifyContent: "center",
                                        p: 1.5,
                                        borderRadius: 2,
                                        bgcolor:
                                            theme.palette.mode === "dark"
                                                ? "rgba(255,255,255,0.04)"
                                                : "rgba(15,23,42,0.04)",
                                    })}
                                >
                                    <Box
                                        component="img"
                                        src={semenarnaLogo}
                                        alt="Semenarna"
                                        sx={(theme) => ({
                                            maxWidth: "85%",
                                            height: "auto",
                                            filter:
                                                theme.palette.mode === "dark"
                                                    ? "brightness(1.1)"
                                                    : "invert(1)",
                                        })}
                                    />
                                </Link>
                            </Box>
                        )}
                    </Drawer>
                )}

                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        minWidth: 0,
                        bgcolor: "background.default",
                        minHeight: "100vh",
                        marginLeft: isMobile
                            ? 0
                            : open
                            ? `${DRAWER_WIDTH}px`
                            : `${COLLAPSED_WIDTH}px`,
                        transition: "margin 0.3s ease",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Toolbar sx={{ minHeight: { xs: 64, sm: 68 }, flexShrink: 0 }} />
                    <Box
                        sx={{
                            flex: 1,
                            p: { xs: 2, sm: 3, md: 4 },
                        }}
                    >
                        {children}
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default Layout;